import { Annotation, AnnotationSet } from "@/types/annotations";
import { ANNOTATION_TYPES, AnnotationType, Point } from "./types";
import { isManualBBoxAnnotation } from "./Segmentation/helpers";

/**
 * Annotation type
 * - Segmentation -> Keypoints + bbox + segmentation + measureBoundaryPixels (optional)
 * - KeyPoints -> Plant-center annotations, optionally limited by `measureBoundaryPixels`
 */

export const annotationExtractor = (
  rawAnnotationSet: AnnotationSet,
  preferredAnnotationType?: AnnotationType
): { list: Annotation[]; measureBoundaryPixels?: Point[]; annotationType: AnnotationType } => {
  const isEmpty = rawAnnotationSet.annotations.length === 0;
  const containsKeypoints = areKeypointAnnotations(rawAnnotationSet);
  const containsSegmentation = areSegmentationAnnotations(rawAnnotationSet);
  const containsOnlyBoxes =
    !isEmpty && rawAnnotationSet.annotations.every((annotation) => Boolean(annotation.bbox));

  let annotationType: AnnotationType = ANNOTATION_TYPES.UNKNOWN;
  if (preferredAnnotationType === ANNOTATION_TYPES.KEY_POINTS && (containsKeypoints || isEmpty)) {
    annotationType = ANNOTATION_TYPES.KEY_POINTS;
  } else if (
    preferredAnnotationType === ANNOTATION_TYPES.SEGMENTATION &&
    (containsOnlyBoxes || isEmpty)
  ) {
    annotationType = ANNOTATION_TYPES.SEGMENTATION;
  } else if (containsSegmentation) {
    annotationType = ANNOTATION_TYPES.SEGMENTATION;
  } else if (containsKeypoints || isEmptyBoundaryAnnotation(rawAnnotationSet)) {
    // Empty boundary = no-model stand count (has boundary but no annotations yet).
    annotationType = ANNOTATION_TYPES.KEY_POINTS;
  }

  if (annotationType === ANNOTATION_TYPES.UNKNOWN) return { list: [], annotationType };
  return {
    list: rawAnnotationSet.annotations,
    measureBoundaryPixels: rawAnnotationSet.measureBoundaryPixels,
    annotationType,
  };
};

const isEmptyBoundaryAnnotation = (rawAnnotationSet: AnnotationSet) => {
  return (
    rawAnnotationSet.measureBoundaryPixels &&
    rawAnnotationSet.measureBoundaryPixels.length > 0 &&
    rawAnnotationSet.annotations.length === 0
  );
};

const areKeypointAnnotations = (rawAnnotationSet: AnnotationSet) => {
  return (
    rawAnnotationSet.annotations.length > 0 &&
    rawAnnotationSet.annotations.every(
      ({ category, keypoints }) =>
        category.name === "plant" &&
        (Boolean(rawAnnotationSet.measureBoundaryPixels?.length) || Boolean(keypoints?.length))
    )
  );
};

const areSegmentationAnnotations = (rawAnnotationSet: AnnotationSet) => {
  return (
    rawAnnotationSet.annotations.length > 0 &&
    rawAnnotationSet.annotations.every(
      (annotation) =>
        (annotation.bbox && (annotation.segmentation || !annotation.keypoints?.length)) ||
        isManualBBoxAnnotation(annotation)
    )
  );
};
